/**
 * Block Editor Helpers
 *
 * Pure computation functions extracted from BlockEditor.tsx for testability.
 * No React state, no DOM, no side effects.
 */

import {
  type DigitalPageBlock,
  type FormattedLine,
  textToLines,
} from './types';

// ============================================================================
// Undo / Redo History Management
// ============================================================================

export interface HistoryState<T> {
  past: T[];
  future: T[];
}

/**
 * Push the current snapshot onto the undo stack, clearing the redo stack.
 * Caps history at maxSize entries (oldest are dropped).
 */
export function historyPush<T>(
  history: HistoryState<T>,
  snapshot: T,
  maxSize: number = 50
): HistoryState<T> {
  const newPast = [...history.past, snapshot];
  if (newPast.length > maxSize) newPast.shift();
  return { past: newPast, future: [] };
}

/**
 * Undo: pop from past, push current state to future.
 * Returns null if nothing to undo.
 */
export function historyUndo<T>(
  history: HistoryState<T>,
  currentState: T
): { history: HistoryState<T>; restored: T } | null {
  if (history.past.length === 0) return null;
  const newPast = [...history.past];
  const restored = newPast.pop()!;
  return {
    history: {
      past: newPast,
      future: [...history.future, currentState],
    },
    restored,
  };
}

/**
 * Redo: pop from future, push current state to past.
 * Returns null if nothing to redo.
 */
export function historyRedo<T>(
  history: HistoryState<T>,
  currentState: T
): { history: HistoryState<T>; restored: T } | null {
  if (history.future.length === 0) return null;
  const newFuture = [...history.future];
  const restored = newFuture.pop()!;
  return {
    history: {
      past: [...history.past, currentState],
      future: newFuture,
    },
    restored,
  };
}

export function canUndo<T>(history: HistoryState<T>): boolean {
  return history.past.length > 0;
}

export function canRedo<T>(history: HistoryState<T>): boolean {
  return history.future.length > 0;
}

// ============================================================================
// Zoom / Pan Calculations
// ============================================================================

export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 4;
export const ZOOM_STEP = 0.25;

/**
 * Clamp a zoom level to the valid range [ZOOM_MIN, ZOOM_MAX].
 */
export function clampZoom(zoom: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));
}

/**
 * Compute the next zoom level after zooming in one step.
 */
export function zoomIn(current: number): number {
  return clampZoom(current + ZOOM_STEP);
}

/**
 * Compute the next zoom level after zooming out one step.
 */
export function zoomOut(current: number): number {
  return clampZoom(current - ZOOM_STEP);
}

/**
 * Compute a new zoom level from a pinch-to-zoom delta.
 * The delta is typically -wheelEvent.deltaY * 0.01.
 */
export function zoomByDelta(current: number, delta: number): number {
  const raw = current + delta;
  return Math.round(clampZoom(raw) * 100) / 100;
}

/**
 * Compute a new zoom level from a pinch gesture scale factor.
 * Scale > 1 means fingers moved apart (zoom in); < 1 means together (zoom out).
 */
export function zoomByPinchScale(startZoom: number, scale: number): number {
  const raw = startZoom * scale;
  return Math.round(clampZoom(raw) * 100) / 100;
}

/**
 * Determine whether pan offset should be reset (when zoom returns to 1x or below).
 */
export function shouldResetPan(zoomLevel: number): boolean {
  return zoomLevel <= 1;
}

// ============================================================================
// Keyboard Shortcut Mapping
// ============================================================================

export type EditorShortcut = 'undo' | 'redo' | 'save' | null;

/**
 * Map a keyboard event to an editor shortcut.
 * Returns null if the event does not match any known shortcut.
 */
export function matchKeyboardShortcut(event: {
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  key: string;
}): EditorShortcut {
  const isModifier = event.metaKey || event.ctrlKey;
  if (!isModifier) return null;

  const key = event.key.toLowerCase();

  if (key === 'z') {
    return event.shiftKey ? 'redo' : 'undo';
  }

  if (key === 's') {
    return 'save';
  }

  return null;
}

// ============================================================================
// Block Reorder / Renumber
// ============================================================================

/**
 * Renumber blocks sequentially (pageNumber = index + 1).
 */
export function renumberBlocks(blocks: DigitalPageBlock[]): DigitalPageBlock[] {
  return blocks.map((block, index) => ({
    ...block,
    pageNumber: index + 1,
  }));
}

/**
 * Delete a block by id. Returns the original array unchanged if:
 * - The block is not found
 * - It is the last remaining block
 * Renumbers remaining blocks after deletion.
 */
export function deleteBlock(
  blocks: DigitalPageBlock[],
  blockId: string
): DigitalPageBlock[] {
  const index = blocks.findIndex(b => b.id === blockId);
  if (index === -1 || blocks.length <= 1) return blocks;

  const newBlocks = blocks.filter(b => b.id !== blockId);
  return renumberBlocks(newBlocks);
}

/**
 * Reorder blocks after a drag-and-drop operation.
 * Takes the old index and new index and returns renumbered blocks.
 * (Uses the arrayMove from @dnd-kit internally, but here we do our own
 * to keep the helper pure and free of external deps.)
 */
export function reorderBlocks(
  blocks: DigitalPageBlock[],
  fromIndex: number,
  toIndex: number
): DigitalPageBlock[] {
  if (fromIndex === toIndex) return blocks;
  if (fromIndex < 0 || fromIndex >= blocks.length) return blocks;
  if (toIndex < 0 || toIndex >= blocks.length) return blocks;

  const result = [...blocks];
  const [moved] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, moved);
  return renumberBlocks(result);
}

// ============================================================================
// Format Painter
// ============================================================================

/**
 * Extract the indent pattern (array of indent levels) from a block's lines.
 * Returns null if the block has no lines.
 */
export function extractIndentPattern(
  lines: FormattedLine[]
): number[] | null {
  if (lines.length === 0) return null;
  return lines.map(line => line.indent);
}

/**
 * Apply an indent pattern to a block's lines.
 * Only the first N lines (where N = pattern length) are affected;
 * lines beyond the pattern retain their original indent.
 */
export function applyIndentPattern(
  lines: FormattedLine[],
  pattern: number[]
): FormattedLine[] {
  return lines.map((line, i) => ({
    ...line,
    indent: i < pattern.length ? pattern[i] : line.indent,
  }));
}

/**
 * Apply an indent pattern to all blocks.
 */
export function applyIndentPatternToAllBlocks(
  blocks: DigitalPageBlock[],
  pattern: number[]
): DigitalPageBlock[] {
  return blocks.map(block => ({
    ...block,
    lines: applyIndentPattern(block.lines, pattern),
  }));
}

// ============================================================================
// Extraction Insertion
// ============================================================================

/**
 * Insert extracted text (as FormattedLines) into a block's lines at a given index.
 */
export function insertExtractedText(
  existingLines: FormattedLine[],
  extractedText: string,
  insertIndex: number
): FormattedLine[] {
  const newLines = textToLines(extractedText);
  return [
    ...existingLines.slice(0, insertIndex),
    ...newLines,
    ...existingLines.slice(insertIndex),
  ];
}

/**
 * Compute a new insertion index after moving up or down.
 * Clamps to [0, maxIndex].
 */
export function moveInsertionIndex(
  currentIndex: number,
  direction: 'up' | 'down',
  maxIndex: number
): number {
  const delta = direction === 'up' ? -1 : 1;
  return Math.max(0, Math.min(maxIndex, currentIndex + delta));
}

// ============================================================================
// Block Validation
// ============================================================================

/**
 * Check whether a block is empty (all lines have empty text).
 */
export function isBlockEmpty(block: DigitalPageBlock): boolean {
  return block.lines.every(line => line.text.trim() === '');
}

/**
 * Count the total number of non-empty lines across all blocks.
 */
export function countNonEmptyLines(blocks: DigitalPageBlock[]): number {
  return blocks.reduce(
    (total, block) =>
      total + block.lines.filter(line => line.text.trim() !== '').length,
    0
  );
}

/**
 * Check whether blocks have changed compared to the initial state.
 * Simple deep comparison of lines content (ignores id/pageNumber since those
 * are structural, not content changes).
 */
export function hasContentChanged(
  current: DigitalPageBlock[],
  initial: DigitalPageBlock[]
): boolean {
  if (current.length !== initial.length) return true;
  for (let i = 0; i < current.length; i++) {
    const cLines = current[i].lines;
    const iLines = initial[i].lines;
    if (cLines.length !== iLines.length) return true;
    for (let j = 0; j < cLines.length; j++) {
      if (
        cLines[j].text !== iLines[j].text ||
        cLines[j].indent !== iLines[j].indent ||
        (cLines[j].bold ?? false) !== (iLines[j].bold ?? false) ||
        (cLines[j].italic ?? false) !== (iLines[j].italic ?? false)
      ) {
        return true;
      }
    }
  }
  return false;
}

// ============================================================================
// Page Navigation Helpers
// ============================================================================

/**
 * Format a page indicator string like "3 / 10" or "0 / 0".
 */
export function formatPageIndicator(
  currentIndex: number,
  totalPages: number
): string {
  if (totalPages === 0) return '0 / 0';
  return `${currentIndex + 1} / ${totalPages}`;
}

/**
 * Navigate to the previous page. Returns the new index (clamped to 0).
 */
export function prevPageIndex(currentIndex: number): number {
  return Math.max(0, currentIndex - 1);
}

/**
 * Navigate to the next page. Returns the new index (clamped to totalPages - 1).
 */
export function nextPageIndex(
  currentIndex: number,
  totalPages: number
): number {
  return Math.min(totalPages - 1, currentIndex + 1);
}
